import React, { createContext, useEffect, useMemo, useReducer } from 'react';

import type { RetryProps } from '@/components/retryFailedQueries';
import { useCage } from '@/contexts/cage';
import { useCageFilter } from '@/contexts/cage-filter-context';
import { useGetWeightDistributions } from '@/domain/cages/useGetWeightDistributions';
import { toDateString } from '@/utils/date';

import type { WeightDistributionState } from './weightDistributionReducer';
import { initialState, weightDistributionReducer } from './weightDistributionReducer';

type WeightDistributionStateContextType = WeightDistributionState;

type WeightDistributionDispatchContextType = {
    setDistributionType: (distributionType: string) => void;
    setCurrentIndex: (index: number) => void;
    /** Try again for the chart's error state. */
    retry: RetryProps;
};

const WeightDistributionStateContext = createContext<WeightDistributionStateContextType | null>(
    null
);
const WeightDistributionDispatchContext =
    createContext<WeightDistributionDispatchContextType | null>(null);

export function useWeightDistributionState(): WeightDistributionStateContextType {
    const context = React.useContext(WeightDistributionStateContext);
    if (!context) {
        throw new Error(
            'useWeightDistributionState must be used within a WeightDistributionProvider'
        );
    }
    return context;
}

export function useWeightDistributionDispatch(): WeightDistributionDispatchContextType {
    const context = React.useContext(WeightDistributionDispatchContext);
    if (!context) {
        throw new Error(
            'useWeightDistributionDispatch must be used within a WeightDistributionProvider'
        );
    }
    return context;
}

export function WeightDistributionProvider({ children }: { children: React.ReactNode }) {
    // TODO: daypartTag to dayPartTag (in cage-filter-context.tsx)
    const {
        daypartTag: dayPartTag,
        weightUnitTag,
        from,
        to,
        selectedFishGroupId,
    } = useCageFilter();

    const { cageId } = useCage();

    const {
        data: weightDistributions,
        isLoading: loading,
        error,
        isFetching,
        refetch,
    } = useGetWeightDistributions(cageId || 0, {
        from: from ? toDateString(from) : undefined,
        to: to ? toDateString(to) : undefined,
        fishGroupId: selectedFishGroupId,
    });

    // Usar useReducer para manejar todo el estado
    const [state, dispatch] = useReducer(weightDistributionReducer, initialState);

    // Reset preferred index when loading starts
    useEffect(() => {
        if (loading) {
            dispatch({ type: 'RESET_PREFERRED_INDEX' });
        }
    }, [loading]);

    // A cached group answers without loading, so reset on the switch itself: another group's
    // day index would open the chart on an arbitrary day instead of the latest one
    useEffect(() => {
        dispatch({ type: 'RESET_PREFERRED_INDEX' });
    }, [selectedFishGroupId]);

    // Update loading state
    useEffect(() => {
        dispatch({ type: 'SET_LOADING', payload: loading });
    }, [loading]);

    // Update error state
    useEffect(() => {
        dispatch({ type: 'SET_ERROR', payload: error });
    }, [error]);

    // Update data when it changes
    useEffect(() => {
        if (weightDistributions && dayPartTag && weightUnitTag) {
            dispatch({
                type: 'SET_DATA',
                payload: {
                    data: weightDistributions,
                    dayPartTag,
                    weightUnitTag,
                },
            });
        }
    }, [weightDistributions, dayPartTag, weightUnitTag]);

    const dispatchContext = useMemo(
        () => ({
            setDistributionType: (distributionType: string) => {
                dispatch({ type: 'SET_DISTRIBUTION_TYPE', payload: distributionType });
            },
            setCurrentIndex: (index: number) => {
                dispatch({ type: 'SET_PREFERRED_INDEX', payload: index });
            },
            retry: { onRetry: refetch, isRetrying: isFetching ?? false },
        }),
        [refetch, isFetching]
    );

    return (
        <WeightDistributionStateContext value={state}>
            <WeightDistributionDispatchContext value={dispatchContext}>
                {children}
            </WeightDistributionDispatchContext>
        </WeightDistributionStateContext>
    );
}
